/**
 * API client para os endpoints de provisionamento de tenants AWS Org (TASK-2435).
 *
 * Backend: src/consoleai/aws_org_provisioning_routes.py (TASK-2434).
 * Spec Pydantic: src/consoleai/api_models/provisioning.py (TASK-2430).
 *
 * Endpoints expostos:
 *   POST   /api/v1/admin/tenants/provision
 *   GET    /api/v1/admin/tenants/jobs
 *   GET    /api/v1/admin/tenants/jobs/{id}
 *   POST   /api/v1/admin/tenants/jobs/{id}/retry
 *   POST   /api/v1/admin/tenants/jobs/{id}/cancel
 *   GET    /api/v1/admin/aws-ou-tree
 */

import { api } from './client'

/** Nome de ambiente — backend agora aceita string custom (regex
 * `^[A-Z][A-Z0-9_-]*$`, 2-8 chars). Manter como string permite ao
 * frontend passar nomes digitados pelo usuario via "Outro" no
 * AddSubTenantModal alem dos 4 padrao. */
export type EnvName = string

/** Os 4 ambientes "padrao" da convencao Amazon Inovare — usados como
 * checkboxes fixos. Outros nomes (STG, SANDBOX, PERF, ...) entram via
 * campo "Outro" e respeitam EnvName regex. */
export const STANDARD_ENV_OPTIONS = ['DEV', 'QA', 'HML', 'PRD'] as const
export const ENV_OPTIONS: readonly EnvName[] = STANDARD_ENV_OPTIONS

/** Regex de validacao para envs custom (espelha api_models/provisioning.py:EnvName). */
export const ENV_NAME_REGEX = /^[A-Z][A-Z0-9_-]*$/

/** Comprimento maximo do nome de env custom (cap acordado v1.32.0). */
export const ENV_NAME_MAX_LENGTH = 8

/** Sugestoes pre-definidas para o "Outro" (chips que preenchem o input). */
export const ENV_SUGGESTIONS = [
  'STG',
  'SBOX',
  'UAT',
  'PERF',
  'DR',
  'LOAD',
  'SANDBOX',
] as const

/** Reservados — espelha RESERVED_ENV_NAMES no backend. Bloquear na UI evita
 * a ida ao servidor. */
export const RESERVED_ENV_NAMES: readonly string[] = [
  'DEFAULT',
  'AWS',
  'ROOT',
  'MASTER',
  'ALL',
]

export const PROJECT_NAME_REGEX = /^[A-Z][A-Z0-9]+$/

export interface SubTenantSpec {
  name: string
  environments: EnvName[]
}

export interface TenantSpec {
  project_name: string
  direct_environments: EnvName[]
  is_saas: boolean
  sub_tenants: SubTenantSpec[]
  cost_center?: string | null
  tags_extra?: Record<string, string>
}

/** Spec persistido em partial provisioning_jobs (add_env, add_sub, enable_saas). */
export interface PartialJobSpec {
  partial_job_type: 'add_environment' | 'add_sub_tenant' | 'enable_saas'
  tenant_id: number
  tenant_name: string
  /** Project name limpo (post-strip de env suffix) — disponivel a partir de v1.24.7. */
  project_name?: string
  environment?: EnvName
  sub_tenant?: SubTenantSpec
}

export type AnyJobSpec = Partial<TenantSpec> & Partial<PartialJobSpec>

export type ProvisioningJobStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'

export type ProvisioningStepStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'skipped'

export interface ProvisioningStepResponse {
  step_index: number
  step_type: string
  status: ProvisioningStepStatus
  step_input?: unknown
  step_output?: unknown
  aws_resource_id?: string | null
  error_message?: string | null
  attempt_count: number
  started_at?: string | null
  completed_at?: string | null
}

export interface CreatedAccountInfo {
  /** Nome real da conta na AWS (ex: "goandroid-prd"). */
  account_name: string
  /** AccountId AWS (12 digitos). Null se step ainda nao executou. */
  account_id: string | null
  /** True se a conta ainda esta na tabela tenants (= esta viva). */
  is_alive: boolean
}

export interface ProvisioningJobResponse {
  id: number
  /** Pode ser TenantSpec (full) ou PartialJobSpec (add_env/sub/saas). */
  tenant_spec: AnyJobSpec
  status: ProvisioningJobStatus
  total_steps: number
  completed_steps: number
  current_step_index?: number | null
  error_message?: string | null
  created_at?: string | null
  started_at?: string | null
  completed_at?: string | null
  created_by?: string | null
  steps?: ProvisioningStepResponse[]
  /** Passos que o job PULOU, com o motivo. Passo pulado nao aparece no
   * `status` (o worker grava 'completed' e poe o motivo em `step_output`),
   * entao sem esta lista o cartao de sucesso mente por omissao. So na rota de
   * detalhe. Ver TASK-10214. */
  ressalvas?: string[]
  /** Outcome real: contas que o job efetivamente criou (de create_account
   * step_input + wait_account_active aws_resource_id). Cross-referenced com
   * tabela tenants pra is_alive. Vem so na rota de listagem (nao no detail). */
  created_accounts?: CreatedAccountInfo[]
}

export interface ProvisioningJobsListResponse {
  items: ProvisioningJobResponse[]
  total: number
  limit: number
  offset: number
}

export interface AwsOuNode {
  id: number | string
  ou_id: string
  name: string
  parent_ou_id?: string | null
  kind?: string | null
  is_legacy?: boolean
  aws_path?: string | null
  children?: AwsOuNode[]
  [key: string]: unknown
}

export interface AwsOuTreeResponse {
  nodes: AwsOuNode[]
}

export const awsOrgProvisioningApi = {
  provision: (spec: TenantSpec) =>
    api
      .post<{ job_id: number }>('/v1/admin/tenants/provision', spec)
      .then((r) => r.data),

  listJobs: (params: {
    status?: ProvisioningJobStatus
    limit?: number
    offset?: number
  } = {}) =>
    api
      .get<ProvisioningJobsListResponse>('/v1/admin/tenants/jobs', { params })
      .then((r) => r.data),

  getJob: (id: number) =>
    api
      .get<ProvisioningJobResponse>(`/v1/admin/tenants/jobs/${id}`)
      .then((r) => r.data),

  retryJob: (id: number, fromStepIndex: number = 0) =>
    api
      .post(`/v1/admin/tenants/jobs/${id}/retry`, {
        from_step_index: fromStepIndex,
      })
      .then((r) => r.data),

  cancelJob: (id: number) =>
    api
      .post(`/v1/admin/tenants/jobs/${id}/cancel`)
      .then((r) => r.data),

  getOuTree: (includeLegacy: boolean = true) =>
    api
      .get<AwsOuTreeResponse>('/v1/admin/aws-ou-tree', {
        params: { include_legacy: includeLegacy },
      })
      .then((r) => r.data),

  enableSaas: (tenantId: number, firstSub?: SubTenantSpec) =>
    api
      .post(
        `/v1/admin/tenants/${tenantId}/enable-saas`,
        firstSub ? { first_sub_tenant: firstSub } : undefined,
      )
      .then((r) => r.data),

  addSubTenant: (tenantId: number, sub: SubTenantSpec) =>
    api
      .post(`/v1/admin/tenants/${tenantId}/add-sub-tenant`, sub)
      .then((r) => r.data),

  /** v1.32.1+: aceita lista de envs em UM job atomico. Wrapper preferido. */
  addEnvironments: (tenantId: number, environments: EnvName[]) =>
    api
      .post(`/v1/admin/tenants/${tenantId}/add-environment`, { environments })
      .then((r) => r.data),

  /** Wrapper legado — chama addEnvironments com [environment]. Mantido pra
   * call sites antigos que ainda passam 1 env. */
  addEnvironment: (tenantId: number, environment: EnvName) =>
    api
      .post(`/v1/admin/tenants/${tenantId}/add-environment`, { environment })
      .then((r) => r.data),

  renameOu: (ouId: string, newName: string, confirmName: string) =>
    api
      .post<{ job_id: number }>(`/v1/admin/ou/${ouId}/rename`, {
        new_name: newName,
        confirm_name: confirmName,
      })
      .then((r) => r.data),

  /** Previa do rename de ambiente. SO LEITURA — nao agenda job.
   *
   * O e-mail derivado vem do servidor de proposito, e nao de uma copia da
   * regra em TypeScript: `derivar_alvo` reusa o `_email()` do provisionamento
   * justamente para as duas regras nao divergirem no primeiro ajuste. E o
   * `conflito` (livre / mesma_conta / outra_conta / fora_do_padrao) nao tem
   * como ser sabido no navegador — depende do estado da AWS. */
  renamePreview: (tenantId: number, newName: string) =>
    api
      .get<RenamePreview>(
        `/v1/admin/tenants/${tenantId}/rename-preview`,
        { params: { new_name: newName } },
      )
      .then((r) => r.data),

  renameEnvironment: (tenantId: number, newName: string, confirmName: string) =>
    api
      .post<{ job_id: number } & RenamePreview>(
        `/v1/admin/tenants/${tenantId}/rename`,
        { new_name: newName, confirm_name: confirmName },
      )
      .then((r) => r.data),
}

/** Conflito do e-mail derivado, classificado pelo servidor ANTES de escrever. */
export type ConflitoEmail =
  | 'livre'
  | 'mesma_conta'
  | 'outra_conta'
  | 'fora_do_padrao'

export interface RenamePreview {
  novo_nome: string
  nome_anterior: string
  novo_email: string
  novo_account_name: string
  conflito: ConflitoEmail
}

/** Conflitos que IMPEDEM o rename. `mesma_conta` nao impede: o e-mail ja e o
 *  da propria conta, entao os passos de troca se pulam sozinhos. */
export const CONFLITOS_QUE_IMPEDEM: readonly ConflitoEmail[] = [
  'outra_conta',
  'fora_do_padrao',
]

/**
 * Conta total de "passos" (steps) de provisionamento que o backend deve criar
 * para um TenantSpec dado.
 *
 * Espelha (best-effort) o que o ProvisioningService faz, para que a tela de
 * Revisão consiga mostrar uma estimativa de progresso ANTES do POST.
 *
 * Por (project, env) tuple: ~11 steps (email + ses_verify + account +
 * wait_account + move_to_ou + iam_user + secret + tags + ses_per_account +
 * notify + finalize). Mais 1 root OU + N env sub-OUs.
 *
 * Para SaaS: tambem cria 1 SaaS-OU + (sub_tenant × env) tuplas.
 */
export function computeEstimatedSteps(spec: TenantSpec): number {
  const PER_ENV_STEPS = 11
  const tuples = countEnvTuples(spec)
  // 1 root OU + N env sub-OUs (direct envs) + (1 SaaS OU se SaaS)
  const ouSteps =
    1 +
    spec.direct_environments.length +
    (spec.is_saas ? 1 + spec.sub_tenants.length : 0) +
    spec.sub_tenants.reduce((acc, st) => acc + st.environments.length, 0)
  return ouSteps + tuples * PER_ENV_STEPS
}

/** Conta quantos pares (tenant, env) o spec produz (direto + sub-tenants). */
export function countEnvTuples(spec: TenantSpec): number {
  const direct = spec.direct_environments.length
  const subs = spec.sub_tenants.reduce(
    (acc, st) => acc + st.environments.length,
    0,
  )
  return direct + subs
}

/** Estimativa de duração legível (~30s por env tuple). */
export function estimateDuration(spec: TenantSpec): string {
  const tuples = countEnvTuples(spec)
  if (tuples === 0) return '—'
  const minSec = tuples * 25
  const maxSec = tuples * 45
  const minMin = Math.max(1, Math.round(minSec / 60))
  const maxMin = Math.max(minMin + 1, Math.round(maxSec / 60))
  return `${minMin}-${maxMin} min`
}
